import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import WorkspaceSelector from './WorkspaceSelector';
// Theme is applied globally via CSS

const Login: React.FC = () => {
  // Feature flag to show/hide email/password login
  const SHOW_EMAIL_PASSWORD_LOGIN = false;
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [workspaceSelection, setWorkspaceSelection] = useState<{
    requiresWorkspaceSelection: boolean;
    workspaces: Array<{ id: string; slug: string; name: string; workspaceRole?: string }>;
    user: { id: string; email: string; isPortalAdmin?: boolean };
  } | null>(null);
  const { user, login, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is authenticated but needs workspace selection
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && !isAuthenticated) {
      // User has token but isn't authenticated - might need workspace selection
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        if (tokenData.workspaceIds && Array.isArray(tokenData.workspaceIds) && tokenData.workspaceIds.length > 1 && !tokenData.workspaceId) {
          // User needs workspace selection - fetch workspaces and show selector
          fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }).then(res => res.json()).then(userData => {
            if (userData.workspaces && userData.workspaces.length > 1) {
              setWorkspaceSelection({
                requiresWorkspaceSelection: true,
                workspaces: userData.workspaces.map((w: any) => ({
                  id: w.id,
                  slug: w.slug,
                  name: w.name,
                  workspaceRole: w.role
                })),
                user: {
                  id: userData.id,
                  email: userData.email,
                  isPortalAdmin: userData.isPortalAdmin || false
                }
              });
            }
          }).catch(err => {
            console.error('Error fetching user workspaces:', err);
          });
        }
      } catch (e) {
        // Token parsing failed, ignore
      }
    }
  }, [isAuthenticated]);

  // Check for error in URL params (from OAuth redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const tokenParam = urlParams.get('token');
    const tempTokenParam = urlParams.get('tempToken');
    const workspacesParam = urlParams.get('workspaces');
    
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (tempTokenParam && workspacesParam) {
      // Handle OAuth with workspace selection
      try {
        const workspaces = JSON.parse(decodeURIComponent(workspacesParam));
        // Store temp token but don't set user/auth state yet
        localStorage.setItem('authToken', tempTokenParam);
        
        // Get user info from temp token
        const tokenData = JSON.parse(atob(tempTokenParam.split('.')[1]));
        const user = { id: tokenData.id, email: tokenData.email, isPortalAdmin: tokenData.isPortalAdmin || false };
        
        setWorkspaceSelection({
          requiresWorkspaceSelection: true,
          workspaces: workspaces,
          user: user
        });
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        setError('Failed to parse workspace information');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (tokenParam) {
      // Handle OAuth callback - check if portal admin
      const urlParams = new URLSearchParams(window.location.search);
      const isPortal = urlParams.get('portal') === 'true';
      
      loginWithToken(tokenParam).then((result) => {
        if (result && result.requiresWorkspaceSelection) {
          // Show workspace selector
          setWorkspaceSelection(result);
        } else if (isPortal) {
          navigate('/portal');
        } else {
          navigate('/');
        }
      }).catch((err) => {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMessage);
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loginWithToken, navigate]);

  // Redirect if already authenticated (but not if workspace selection is needed)
  // Only redirect if we're actually on the login page - preserve current route on refresh
  useEffect(() => {
    // Only redirect if we're on the login page
    if (isAuthenticated && user && !workspaceSelection && location.pathname === '/login') {
      // Check if there's a location state with a return path (from ProtectedRoute redirect)
      const state = location.state as { from?: string } | null;
      const returnPath = state?.from;
      
      // Portal admins go to portal, others go to workspace or return path
      if (user.isPortalAdmin) {
        navigate('/portal', { replace: true });
      } else if (returnPath) {
        navigate(returnPath, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, workspaceSelection, location.pathname, location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result && result.requiresWorkspaceSelection) {
        // Show workspace selector
        setWorkspaceSelection(result);
      } else {
        // Single workspace or auto-selected, navigate to home
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Determine backend URL based on current environment
    // In production: use relative path (same origin) - this ensures we always use the correct production backend
    // In development: use explicit localhost backend URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isDevelopment 
      ? 'http://localhost:5001'  // Explicit backend URL for development
      : '';                       // Empty string = relative path (same origin) for production
    
    // Use relative path in production to ensure we always hit the correct backend
    // This prevents issues when dev credentials are configured in Google OAuth
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  // Show workspace selector if needed
  if (workspaceSelection) {
    return (
      <WorkspaceSelector
        workspaces={workspaceSelection.workspaces}
        user={workspaceSelection.user}
        onWorkspaceSelected={() => {
          setWorkspaceSelection(null);
          navigate('/');
        }}
      />
    );
  }

  return (
    <div className="form-container login-container">
      <h1 className="login-title">Login</h1>
      
      {error && (
        <div className="login-error">
          {error}
        </div>
      )}

      {SHOW_EMAIL_PASSWORD_LOGIN && (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-submit-button"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      )}

      {SHOW_EMAIL_PASSWORD_LOGIN && (
        <div className="login-divider">
          <div className="login-divider-text">or</div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: SHOW_EMAIL_PASSWORD_LOGIN ? '0' : '2rem' }}>
        <button
          onClick={handleGoogleLogin}
          className="btn login-google-button"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <g fill="#000" fillRule="evenodd">
              <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
              <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.08-1.79 2.68l2.85 2.2c2.01-1.86 3.62-4.58 3.62-7.38z" fill="#4285F4"/>
              <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.85-2.2c-.76.53-1.78.9-3.11.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
            </g>
          </svg>
          Sign in with Google
        </button>
      </div>
    </div>
  );
};

export default Login;

